import { fetchRest, getApiBaseUrl } from "@/config/api";
import type { PrestashopImageOverride, PrestashopMelOverrides } from "@/types/prestashop";
import type { ShopifyCatalogPage, ShopifyConnection, ShopifyProduct } from "@/types/shopify";

const baseUrl = () => `${getApiBaseUrl()}/shopify`;

async function readJson<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const rawMessage = (data as { message?: unknown }).message;
    const message = Array.isArray(rawMessage)
      ? rawMessage.join(", ")
      : String(rawMessage || `Erreur HTTP ${response.status}`);
    throw new Error(message);
  }
  return data as T;
}

export async function syncLinkedProductsFromShopify(afterId = 0) {
  return readJson<{
    updated: number;
    skipped: number;
    failed: number;
    nextAfterId: number | null;
    results: Array<{ idProduit: number; action: string; reason?: string }>;
  }>(await fetchRest(`${baseUrl()}/products/sync-from-shopify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ afterId }),
  }));
}

export async function getShopifyConnection() {
  const response = await fetchRest(`${baseUrl()}/connection`);
  return readJson<ShopifyConnection>(response);
}

export async function claimShopifyInstall(pendingId: string) {
  const response = await fetchRest(`${baseUrl()}/install/claim`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pendingId }),
  });
  return readJson<{ shopUrl: string }>(response);
}

export async function getShopifyAuthorizeUrl(shop: string) {
  const query = new URLSearchParams({ shop });
  const response = await fetchRest(
    `${baseUrl()}/oauth/authorize?${query.toString()}`
  );
  return readJson<{ authorizeUrl: string }>(response);
}

export async function disconnectShopify() {
  const response = await fetchRest(`${baseUrl()}/connection`, {
    method: "DELETE",
  });
  return readJson<{ success: boolean }>(response);
}

export type ShopifyMappingType = "color" | "category" | "composition" | "brand";

export interface ShopifySettings {
  defaultGender: "femme" | "homme" | "enfant" | "aucun" | null;
  defaultProvenanceId: number | null;
  setupCompleted: boolean;
  autoSyncFromShopify: boolean;
  autoSyncOrdersToShopify: boolean;
}

export interface ShopifyMapping {
  sourceLabel: string;
  sourceLabelNormalized: string;
  targetId: number;
  targetLabel: string | null;
}

export interface ShopifyMappingTarget {
  id: number;
  label: string;
  rootId: number | null;
  isDefault?: boolean;
}

export interface ShopifyPendingMappings {
  color: string[];
  colorHexByName: Record<string, string>;
  category: string[];
  composition: string[];
  brand: string[];
  scannedProducts: number;
  incomplete: boolean;
}

export async function getShopifyPendingMappings() {
  return readJson<ShopifyPendingMappings>(await fetchRest(`${baseUrl()}/mappings/pending`));
}

export async function createShopifyBrandMapping(sourceLabel: string, brandName: string) {
  return readJson<ShopifyMapping & { createdBrand: boolean }>(await fetchRest(`${baseUrl()}/mappings/brand/create-target`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sourceLabel, brandName }),
  }));
}

export async function getShopifySettings() {
  return readJson<ShopifySettings>(await fetchRest(`${baseUrl()}/settings`));
}

export async function getShopifyProvenances() {
  return readJson<Array<{ id: number; label: string }>>(await fetchRest(`${baseUrl()}/settings/provenances`));
}

export async function saveShopifySettings(
  defaultGender: NonNullable<ShopifySettings["defaultGender"]>,
  defaultProvenanceId: number,
  autoSyncFromShopify?: boolean,
  autoSyncOrdersToShopify?: boolean,
) {
  return readJson<ShopifySettings>(await fetchRest(`${baseUrl()}/settings`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      defaultGender,
      defaultProvenanceId,
      ...(autoSyncFromShopify !== undefined ? { autoSyncFromShopify } : {}),
      ...(autoSyncOrdersToShopify !== undefined ? { autoSyncOrdersToShopify } : {}),
    }),
  }));
}

export async function exportShopifyOrders() {
  return readJson<{
    exported: number;
    results: Array<{ action: string; orderName: string; reason?: string; idCommande?: number }>;
  }>(await fetchRest(`${baseUrl()}/orders/export`, { method: "POST" }));
}

export async function reregisterShopifyWebhooks() {
  return readJson<{
    success: boolean;
    callbackUrl: string;
    subscriptions: Array<{ topic: string; callbackUrl: string }>;
  }>(await fetchRest(`${baseUrl()}/webhooks/reregister`, { method: "POST" }));
}

export async function getShopifyMappings(type: ShopifyMappingType) {
  return readJson<ShopifyMapping[]>(await fetchRest(`${baseUrl()}/mappings/${type}`));
}

export async function getShopifyMappingTargets(type: ShopifyMappingType) {
  return readJson<ShopifyMappingTarget[]>(await fetchRest(`${baseUrl()}/mappings/${type}/targets`));
}

export async function saveShopifyMapping(type: ShopifyMappingType, sourceLabel: string, targetId: number) {
  return readJson<ShopifyMapping>(await fetchRest(`${baseUrl()}/mappings/${type}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sourceLabel, targetId }),
  }));
}

export async function deleteShopifyMapping(type: ShopifyMappingType, sourceLabel: string) {
  return readJson<{ success: boolean; updatedProducts: number }>(await fetchRest(`${baseUrl()}/mappings/${type}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sourceLabel }),
  }));
}

export interface ShopifyColorMapping {
  sourceLabel: string;
  sourceLabelNormalized: string;
  idCouleur: number;
  couleurLabel: string | null;
}

export async function getShopifyColorMappings() {
  const response = await fetchRest(`${baseUrl()}/color-mappings`);
  return readJson<ShopifyColorMapping[]>(response);
}

export async function saveShopifyColorMapping(
  sourceLabel: string,
  idCouleur: number,
) {
  const response = await fetchRest(`${baseUrl()}/color-mappings`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sourceLabel, idCouleur }),
  });
  return readJson<ShopifyColorMapping>(response);
}

export async function deleteShopifyColorMapping(sourceLabel: string) {
  const response = await fetchRest(`${baseUrl()}/color-mappings`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sourceLabel }),
  });
  return readJson<{
    success: boolean;
    sourceLabel: string;
    updatedProducts?: number;
  }>(response);
}

export interface ShopifyCategoryMapping {
  sourceLabel: string;
  sourceLabelNormalized: string;
  idCategorie: number;
  categorieLabel: string | null;
}

export async function getShopifyCategoryMappings() {
  const response = await fetchRest(`${baseUrl()}/category-mappings`);
  return readJson<ShopifyCategoryMapping[]>(response);
}

export async function saveShopifyCategoryMapping(
  sourceLabel: string,
  idCategorie: number,
) {
  const response = await fetchRest(`${baseUrl()}/category-mappings`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sourceLabel, idCategorie }),
  });
  return readJson<ShopifyCategoryMapping>(response);
}

export async function deleteShopifyCategoryMapping(sourceLabel: string) {
  const response = await fetchRest(`${baseUrl()}/category-mappings`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sourceLabel }),
  });
  return readJson<{
    success: boolean;
    sourceLabel: string;
    updatedProducts?: number;
  }>(response);
}

export async function getShopifyProducts(
  page: number,
  limit: number,
  search?: string
) {
  const query = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  const trimmedSearch = search?.trim();
  if (trimmedSearch) {
    query.set("search", trimmedSearch);
  }
  const response = await fetchRest(`${baseUrl()}/products?${query.toString()}`);
  return readJson<ShopifyCatalogPage>(response);
}

export interface ShopifyCatalogStatus {
  ready: boolean;
  syncing: boolean;
  scanned: number;
  lastSyncAt: string | null;
}

export async function getShopifyCatalogStatus() {
  return readJson<ShopifyCatalogStatus>(await fetchRest(`${baseUrl()}/catalog/status`));
}

export async function syncShopifyCatalogNextPage(restart = false) {
  return readJson<ShopifyCatalogStatus & { done: boolean }>(await fetchRest(`${baseUrl()}/catalog/sync-page`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ restart }),
  }));
}

export async function getShopifyProduct(productId: string) {
  const response = await fetchRest(
    `${baseUrl()}/products/${encodeURIComponent(productId)}`
  );
  return readJson<import("@/types/shopify").ShopifyProduct>(response);
}

export async function getShopifyProductVariantStocks(productId: string) {
  const response = await fetchRest(
    `${baseUrl()}/products/${encodeURIComponent(productId)}/variant-stocks`
  );
  return readJson<{
    stocks: import("@/types/shopify").ShopifyVariantStock[];
  }>(response);
}

export async function saveShopifyProductOverrides(
  productGid: string,
  overrides: PrestashopMelOverrides
) {
  const response = await fetchRest(
    `${baseUrl()}/products/${encodeURIComponent(productGid)}/overrides`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(overrides),
    }
  );
  return readJson<{ success: boolean; overrides: PrestashopMelOverrides }>(
    response
  );
}

export async function resetShopifyProductOverrides(productGid: string) {
  const response = await fetchRest(
    `${baseUrl()}/products/${encodeURIComponent(productGid)}/overrides`,
    {
      method: "DELETE",
    }
  );
  return readJson<ShopifyProduct>(response);
}

export async function resetAllShopifyProductOverrides() {
  const response = await fetchRest(`${baseUrl()}/overrides`, {
    method: "DELETE",
  });
  return readJson<{ success: boolean; cleared: number }>(response);
}

export type ShopifyBulkOverridesReport = {
  updated: number;
  failed: number;
  results: Array<{
    shopifyProductGid: string;
    success: boolean;
    message?: string;
  }>;
};

export async function saveShopifyBulkOverrides(
  productGids: string[],
  overrides: PrestashopMelOverrides
) {
  const response = await fetchRest(`${baseUrl()}/products/overrides/bulk`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ productGids, overrides }),
  });
  return readJson<ShopifyBulkOverridesReport>(response);
}

export const SHOPIFY_BULK_BATCH_SIZE = 50;

function chunkProductGids(productGids: string[], batchSize: number) {
  const chunks: string[][] = [];
  for (let index = 0; index < productGids.length; index += batchSize) {
    chunks.push(productGids.slice(index, index + batchSize));
  }
  return chunks;
}

export async function saveShopifyBulkOverridesInBatches(
  productGids: string[],
  overrides: PrestashopMelOverrides,
  options?: {
    batchSize?: number;
    onBatchProgress?: (progress: {
      batchIndex: number;
      batchCount: number;
      batchUpdated: number;
      batchFailed: number;
      updatedSoFar: number;
      failedSoFar: number;
    }) => void;
  }
): Promise<ShopifyBulkOverridesReport> {
  const uniqueGids = Array.from(
    new Set(
      productGids.map((gid) => String(gid || "").trim()).filter(Boolean)
    )
  );
  const batchSize = Math.max(1, options?.batchSize ?? SHOPIFY_BULK_BATCH_SIZE);
  const chunks = chunkProductGids(uniqueGids, batchSize);
  const aggregated: ShopifyBulkOverridesReport = {
    updated: 0,
    failed: 0,
    results: [],
  };

  for (let index = 0; index < chunks.length; index += 1) {
    const report = await saveShopifyBulkOverrides(chunks[index], overrides);
    aggregated.updated += report.updated;
    aggregated.failed += report.failed;
    aggregated.results.push(...report.results);
    options?.onBatchProgress?.({
      batchIndex: index + 1,
      batchCount: chunks.length,
      batchUpdated: report.updated,
      batchFailed: report.failed,
      updatedSoFar: aggregated.updated,
      failedSoFar: aggregated.failed,
    });
  }

  return aggregated;
}

export interface ShopifyImportRow {
  id: string;
  legacyResourceId?: string;
  title?: string;
  name?: string;
  handle?: string;
  vendor?: string;
  productType?: string;
  shopifyCategoryName?: string;
  status?: string;
  tags?: string[];
  createdAt?: string;
  updatedAt?: string;
  description?: string;
  descriptionHtml?: string;
  descriptions?: ShopifyProduct["descriptions"];
  totalInventory?: number;
  variantsCount?: number;
  imageUrl?: string | null;
  imageIds?: string[];
  images?: ShopifyProduct["images"];
  reference?: string;
  barcode?: string | null;
  active?: boolean;
  price?: number;
  wholesalePrice?: number;
  weight?: number;
  options?: ShopifyProduct["options"];
  variants?: ShopifyProduct["variants"];
  collectionNames?: string[];
  compositionLabel?: string;
  metafields?: ShopifyProduct["metafields"];
  overrides?: ShopifyProduct["overrides"];
  brandName?: string;
}

/** La mise en ligne envoie la fiche affichée, pas seulement l'identifiant. */
export function toShopifyImportRow(product: ShopifyProduct): ShopifyImportRow {
  return {
    id: product.id,
    legacyResourceId: product.legacyResourceId,
    title: product.title || product.name,
    name: product.name,
    handle: product.handle,
    vendor: product.vendor,
    productType: product.productType,
    shopifyCategoryName: product.shopifyCategoryName,
    status: product.status,
    tags: product.tags ?? [],
    createdAt: product.createdAt,
    updatedAt: product.updatedAt,
    description: product.description,
    descriptionHtml: product.descriptionHtml,
    descriptions: product.descriptions,
    totalInventory: product.totalInventory,
    variantsCount: product.variantsCount,
    imageUrl: product.imageUrl,
    imageIds: product.imageIds ?? [],
    images: product.images ?? [],
    reference: product.reference,
    barcode: product.barcode,
    active: product.active,
    price: product.price,
    wholesalePrice: product.wholesalePrice,
    weight: product.weight,
    options: product.options ?? [],
    variants: product.variants ?? [],
    collectionNames: product.collectionNames ?? [],
    compositionLabel: product.compositionLabel,
    metafields: product.metafields ?? [],
    overrides: product.overrides ?? {},
    brandName: product.brandName,
  };
}

export async function importShopifyProducts(products: ShopifyImportRow[]) {
  const response = await fetchRest(`${baseUrl()}/products/import`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ products }),
  });
  return readJson<ShopifyImportReport>(response);
}

export async function importShopifyProductsByGids(productGids: string[]) {
  const response = await fetchRest(`${baseUrl()}/products/import`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ productGids }),
  });
  return readJson<ShopifyImportReport>(response);
}

export type ShopifyImportPreview = {
  productGids: string[];
  scanned: number;
  ready: number;
  active: number;
  draft: number;
  existing: number;
  incomplete: number;
  duplicateReferences: number;
  scanIncomplete: boolean;
};

export async function getShopifyImportPreview() {
  const response = await fetchRest(`${baseUrl()}/products/import-preview`);
  return readJson<ShopifyImportPreview>(response);
}

export const SHOPIFY_IMPORT_BATCH_SIZE = 20;

export type ShopifyImportReport = {
  imported: number;
  failed: number;
  results: Array<{
    shopifyProductGid: string;
    success: boolean;
    efashionProductId?: number;
    published?: boolean;
    visibility?: "online" | "offline" | "draft";
    message?: string;
  }>;
};

function chunkProductGidsForImport(
  productGids: string[],
  batchSize: number
): string[][] {
  const chunks: string[][] = [];
  for (let index = 0; index < productGids.length; index += batchSize) {
    chunks.push(productGids.slice(index, index + batchSize));
  }
  return chunks;
}

export async function importShopifyProductRowsInBatches(
  products: ShopifyImportRow[],
  options?: {
    batchSize?: number;
    onBatchProgress?: (progress: {
      batchIndex: number;
      batchCount: number;
      batchImported: number;
      batchFailed: number;
      importedSoFar: number;
      failedSoFar: number;
    }) => void;
    onBatchReport?: (report: ShopifyImportReport) => void;
  }
): Promise<ShopifyImportReport> {
  const batchSize = Math.max(1, options?.batchSize ?? SHOPIFY_IMPORT_BATCH_SIZE);
  const chunks: ShopifyImportRow[][] = [];
  for (let index = 0; index < products.length; index += batchSize) {
    chunks.push(products.slice(index, index + batchSize));
  }
  const aggregated: ShopifyImportReport = {
    imported: 0,
    failed: 0,
    results: [],
  };
  for (let index = 0; index < chunks.length; index += 1) {
    const report = await importShopifyProducts(chunks[index]);
    aggregated.imported += report.imported;
    aggregated.failed += report.failed;
    aggregated.results.push(...report.results);
    options?.onBatchReport?.(report);
    options?.onBatchProgress?.({
      batchIndex: index + 1,
      batchCount: chunks.length,
      batchImported: report.imported,
      batchFailed: report.failed,
      importedSoFar: aggregated.imported,
      failedSoFar: aggregated.failed,
    });
  }
  return aggregated;
}

export async function importShopifyProductsInBatches(
  productGids: string[],
  options?: {
    batchSize?: number;
    onBatchProgress?: (progress: {
      batchIndex: number;
      batchCount: number;
      batchImported: number;
      batchFailed: number;
      importedSoFar: number;
      failedSoFar: number;
    }) => void;
    onBatchReport?: (report: ShopifyImportReport) => void;
  }
): Promise<ShopifyImportReport> {
  const uniqueGids = Array.from(
    new Set(
      productGids.map((gid) => String(gid || "").trim()).filter(Boolean)
    )
  );
  const batchSize = Math.max(1, options?.batchSize ?? SHOPIFY_IMPORT_BATCH_SIZE);
  const chunks = chunkProductGidsForImport(uniqueGids, batchSize);
  const aggregated: ShopifyImportReport = {
    imported: 0,
    failed: 0,
    results: [],
  };

  for (let index = 0; index < chunks.length; index += 1) {
    const report = await importShopifyProductsByGids(chunks[index]);
    aggregated.imported += report.imported;
    aggregated.failed += report.failed;
    aggregated.results.push(...report.results);
    options?.onBatchReport?.(report);
    options?.onBatchProgress?.({
      batchIndex: index + 1,
      batchCount: chunks.length,
      batchImported: report.imported,
      batchFailed: report.failed,
      importedSoFar: aggregated.imported,
      failedSoFar: aggregated.failed,
    });
  }

  return aggregated;
}

export function getShopifyCustomImageUrl(
  productGid: string,
  filename: string
): string {
  return `${baseUrl()}/products/${encodeURIComponent(productGid)}/images/custom/${encodeURIComponent(filename)}`;
}

function shopifyImageKey(url: string | null | undefined): string {
  const raw = String(url || "").trim();
  if (!raw) return "";
  try {
    const parsed = new URL(raw);
    return `${parsed.origin}${parsed.pathname}`.toLowerCase();
  } catch {
    return raw.split("?")[0].toLowerCase();
  }
}

export function resolveShopifyCoverImageUrl(
  product: Pick<ShopifyProduct, "id" | "imageUrl" | "images" | "overrides">
): string | null {
  const saved = product.overrides?.images?.[0];
  if (saved?.type === "shopify" && saved.url) {
    return saved.url;
  }
  if (saved?.type === "custom" && saved.file && product.id) {
    return getShopifyCustomImageUrl(product.id, saved.file);
  }

  const colored = (product.images ?? []).filter(
    (image) => Boolean(image.colorName?.trim()) && image.url,
  );
  const coverKey = shopifyImageKey(product.imageUrl);
  const coverImage = coverKey
    ? colored.find((image) => shopifyImageKey(image.url) === coverKey)
    : undefined;
  if (coverImage?.url) return coverImage.url;
  if (colored[0]?.url) return colored[0].url;
  return product.imageUrl ?? product.images?.[0]?.url ?? null;
}

export async function uploadShopifyCustomImage(
  productGid: string,
  file: File
): Promise<{ file: string }> {
  const body = new FormData();
  body.append("photo", file);
  const response = await fetchRest(
    `${baseUrl()}/products/${encodeURIComponent(productGid)}/images/upload`,
    {
      method: "POST",
      body,
    }
  );
  return readJson<{ file: string }>(response);
}

export async function saveShopifyProductImages(
  productGid: string,
  images: PrestashopImageOverride[]
) {
  const response = await fetchRest(
    `${baseUrl()}/products/${encodeURIComponent(productGid)}/images`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ images }),
    }
  );
  return readJson<{ success: boolean; images: PrestashopImageOverride[] }>(
    response
  );
}
